import CarouselCardSmall from 'components/OfferList/OfferCards/OfferCardSmall/CarouselCardSmall'
import type { ComponentProps } from 'react'
import { useContext, useMemo } from 'react'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import GeoContext from 'contexts/geoContext'
import BedbankOfferCardPriceDetails from './BedbankOfferCardPriceDetails'
import CarouselCardMedium from './OfferCardMedium/CarouselCardMedium'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import useBedbankRates from 'hooks/useBedbankRates'
import { useOfferRatePrice } from 'hooks/Offers/useOfferPriceNew'
import { generateLoyaltyPointsCalculatorBedbankOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'

type InheritedCarouselCardMediumProps = Pick<ComponentProps<typeof CarouselCardMedium>, 'variant' | 'eagerLoadImage'>

interface Props extends InheritedCarouselCardMediumProps {
  offer: App.BedbankOffer | App.BedbankOfferSummary | App.BedbankOfferWithRate
  onClick: () => void
  className?: string
  offerUrl: string
  size: 'small' | 'medium'
  selectedDatesAndRoomsFilter?: App.OfferListFilters
  CtaComponent?: React.ComponentType
}

function BedbankOfferCard(props: Props) {
  const {
    offer,
    onClick,
    className,
    offerUrl,
    size,
    selectedDatesAndRoomsFilter,
    variant,
    eagerLoadImage,
    CtaComponent,
  } = props

  const { currentRegionCode } = useContext(GeoContext)

  const location = [
    offer.property.address.city,
    offer.property.address.countryName,
  ]
    .filter(Boolean)
    .join(', ')

  const [{ hotelOnlyRates }] = useBedbankRates(offer.id)
  const rate = hotelOnlyRates[0]
  const { default: pricing, member: memberPricing } = useOfferRatePrice(offer, rate)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    const propertyFees = pricing?.fees?.find((fee) => fee.type === 'property')?.amount ?? 0
    return [
      generateLoyaltyPointsCalculatorBedbankOptions(rate, {
        price: pricing?.price,
        memberPrice: memberPricing?.price,
        propertyFees,
        offerId: offer.id,
        propertyId: offer.property.id,
        propertyCountryCode: offer.property.address.countryCode,
        propertyState: offer.property.address.stateProvinceCode,
        currentRegionCode,
      }),
    ]
  }, [memberPricing?.price, pricing?.price, rate, pricing?.fees, offer.id, offer.property.id, offer.property.address.countryCode, currentRegionCode, offer.property.address.stateProvinceCode])

  // Only show property name if different from offer name to avoid duplication
  const providerName = offer.property.name !== offer.name ? offer.property.name : undefined

  return (
    <ProductPaletteProvider palette="default">
      {size === 'medium' && <CarouselCardMedium
        variant={variant}
        data-testid="offer-card"
        className={className}
        eagerLoadImage={eagerLoadImage}
        bookmarkButton={<BookmarkButton offer={offer} withLabel="tablet" />}
        image={offer.image}
        images={offer.images}
        location={location}
        title={offer.name}
        providerName={providerName}
        priceDetails={<BedbankOfferCardPriceDetails variant="condensed" offer={offer} selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter} CtaComponent={CtaComponent} />}
        to={offerUrl}
        rating={isOfferRatingDisplayable(offer.property.rating) ? offer.property.rating : undefined}
        onClick={onClick}
        loyaltyPointsElement={<LoyaltyPointsEarn calculationRequests={pointsEarnCalculationRequests} calculationType="estimate" offer={offer} />}
      />}
      {size === 'small' && <CarouselCardSmall
        variant={variant}
        data-testid="offer-card"
        className={className}
        eagerLoadImage={eagerLoadImage}
        bookmarkButton={<BookmarkButton offer={offer} withLabel="tablet" />}
        description={offer.amenities}
        descriptionLines={2}
        image={offer.image}
        images={offer.images}
        location={location}
        title={offer.property.name}
        priceDetails={<BedbankOfferCardPriceDetails offer={offer} selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter} CtaComponent={CtaComponent} />}
        to={offerUrl}
        rating={isOfferRatingDisplayable(offer.property.rating) ? offer.property.rating : undefined}
        onClick={onClick}
        loyaltyPointsElement={<LoyaltyPointsEarn calculationRequests={pointsEarnCalculationRequests} calculationType="estimate" offer={offer} />}
      />}
    </ProductPaletteProvider>
  )
}

export default BedbankOfferCard
